import { deferTracking, sendGAEvent } from '@noths/polaris-client-google-analytics';

/**
 * Isolates the pill row from every other filtering surface on the PLP, per DISC-4661.
 *
 * The sidebar and the Filters drawer both report `plp_filter_facet`, so this name is
 * the single field that separates pill-driven filtering in reporting. Shared with the
 * Redux events map, which stamps it onto the filter events it owns.
 */
export const PLP_FILTER_PILL_NAME = 'plp_filter_pill';

/** Row events describe the pills themselves, drawer events the modal they open. */
const getFeatureDetails = (action: string) => ({
  name: PLP_FILTER_PILL_NAME,
  type: 'facet_pill',
  action,
  page_type: 'plp',
});

const getModalDetails = (action: string) => ({
  name: PLP_FILTER_PILL_NAME,
  type: 'mobile_drawer',
  action,
  page_type: 'plp',
});

const commonEventProps = {
  event: 'custom_event_v2',
  event_name: 'filter_interaction',
  content_type: 'filters',
};

export interface FacetPillOpenPayload {
  facetKey: string;
  facetTitle: string;
  filtersApplied: string;
  index: number;
}

/**
 * Reports a pill being tapped.
 *
 * `index` is the position in the rendered row. Promoted pills are pinned to the front,
 * so it is what lets a promoted pill be compared against a dynamic one rather than
 * against the row average.
 */
export const trackFacetPillOpen = ({
  facetKey,
  facetTitle,
  filtersApplied,
  index,
}: FacetPillOpenPayload) => {
  // Defer off the interaction's critical path: fire-and-forget, does not gate the drawer opening.
  deferTracking(() =>
    sendGAEvent({
      ...commonEventProps,
      event_details: 'open pill',
      field_name: facetTitle,
      item_id: facetKey,
      index,
      filters_applied: filtersApplied,
      feature_details: getFeatureDetails('open facet'),
    }),
  );
};

export interface FacetPillViewResultsPayload {
  facetTitle: string;
  filtersApplied: string;
}

export const trackFacetPillViewResults = ({
  facetTitle,
  filtersApplied,
}: FacetPillViewResultsPayload) => {
  // Defer off the interaction's critical path: fire-and-forget, does not gate the drawer closing.
  deferTracking(() =>
    sendGAEvent({
      ...commonEventProps,
      event_details: 'view results',
      field_name: facetTitle,
      filters_applied: filtersApplied,
      modal_details: getModalDetails('view results'),
    }),
  );
};

/**
 * Reports the drawer being dismissed by the X or the overlay rather than by the CTA.
 *
 * Paired with the open event this is what gives us opened-without-applying, which no
 * other filtering surface reports.
 */
export const trackFacetPillClose = ({ facetTitle }: { facetTitle: string }) => {
  // Defer off the interaction's critical path: fire-and-forget, does not gate the drawer closing.
  deferTracking(() =>
    sendGAEvent({
      ...commonEventProps,
      event_details: 'close pill',
      field_name: facetTitle,
      modal_details: getModalDetails('close'),
    }),
  );
};

export interface FacetPillImpressionPayload {
  facetTitles: string[];
  promotedCount: number;
}

/**
 * Reports the pill row reaching the viewport.
 *
 * Sent once per mount rather than per pill: the row is the unit being measured, and
 * `promoted_count` against `item_count` is what says how much of it was merchandised
 * rather than drawn from the response.
 */
export const trackFacetPillImpression = ({
  facetTitles,
  promotedCount,
}: FacetPillImpressionPayload) => {
  // Defer off the critical path: fire-and-forget, and it gates nothing.
  deferTracking(() =>
    sendGAEvent({
      ...commonEventProps,
      event_details: 'pill impression',
      field_value: facetTitles.join(', '),
      item_count: facetTitles.length,
      promoted_count: promotedCount,
      feature_details: getFeatureDetails('impression'),
    }),
  );
};

/**
 * Reports the row being moved, by swipe or by arrow.
 *
 * Fired once per page view by the caller. A scroll says the customer looked past the
 * pills that fitted, which is the signal; how far they went is not.
 */
export const trackFacetPillScroll = () => {
  // Defer off the critical path: fire-and-forget, and it gates nothing.
  deferTracking(() =>
    sendGAEvent({
      ...commonEventProps,
      event_details: 'pill scroll',
      feature_details: getFeatureDetails('scroll'),
    }),
  );
};
